import type { CellDef, RowInput } from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import type { SaldoPorZonaCliente, SaldoPorZonaInforme } from '../services/cuentaCorrienteService';
import { formatCurrency, formatDate, formatPesosInput } from './formatters';

export interface SaldoPorZonaListadoOpts {
  incluirMovimientos: boolean;
}

const AZUL: [number, number, number] = [31, 78, 121];
const CELESTE: [number, number, number] = [214, 227, 240];
const GRIS_TEXTO: [number, number, number] = [89, 89, 89];

const importe = (n: number | null | undefined): string => formatPesosInput(Number(n) || 0);
const importeOVacio = (n: number | null | undefined): string => (Number(n) ? importe(n) : '');

const localidad = (c: SaldoPorZonaCliente): string =>
  [c.provincia, c.ciudad, c.direccion].map((x) => x?.trim()).filter(Boolean).join('  ·  ');

const nombreZona = (codigoRuta: number | null | undefined, nombreRuta: string): string =>
  codigoRuta != null ? `${nombreRuta}  (${codigoRuta})` : nombreRuta;

const fila = (content: string, colSpan: number, styles: CellDef['styles'] = {}): CellDef => ({
  content,
  colSpan,
  styles
});

function filasDetalladas(informe: SaldoPorZonaInforme): RowInput[] {
  const rows: RowInput[] = [];
  const desdeLabel = formatDate(informe.fechaDesde);
  for (const zona of informe.zonas) {
    rows.push([
      fila(nombreZona(zona.codigoRuta, zona.nombreRuta), 7, {
        fillColor: AZUL,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      })
    ]);
    for (const c of zona.clientes) {
      const telefono = c.telefono?.trim() ? `  ·  Tel. ${c.telefono.trim()}` : '';
      rows.push([
        fila(c.nombre, 4, { fillColor: CELESTE, fontStyle: 'bold', fontSize: 8 }),
        fila(`Nº ${c.nroCliente}${telefono}`, 3, { fillColor: CELESTE, halign: 'right', fontSize: 7.5 })
      ]);
      const loc = localidad(c);
      if (loc) {
        rows.push([fila(loc, 7, { fontSize: 7, textColor: GRIS_TEXTO })]);
      }
      rows.push([
        fila(`Saldo anterior al ${desdeLabel}:`, 6, { halign: 'right', fontStyle: 'italic' }),
        fila(importe(c.saldoAnterior), 1, { halign: 'right', fontStyle: 'italic' })
      ]);
      for (const m of c.movimientos) {
        rows.push([
          formatDate(m.fechaEmision),
          m.comprobante,
          m.descripcion ?? '',
          { content: importe(m.saldoComp), styles: { textColor: GRIS_TEXTO } },
          importeOVacio(m.debe),
          importeOVacio(m.haber),
          importe(m.saldo)
        ]);
      }
      rows.push([
        fila(`Saldo: ${c.nombre}`, 6, { halign: 'right', fontStyle: 'bold' }),
        fila(importe(c.saldo), 1, { halign: 'right', fontStyle: 'bold' })
      ]);
    }
    rows.push([
      fila(`Total Zona ${zona.nombreRuta}`, 6, { halign: 'right', fontStyle: 'bold', textColor: AZUL, fontSize: 8.5 }),
      fila(formatCurrency(zona.total), 1, { halign: 'right', fontStyle: 'bold', textColor: AZUL, fontSize: 8.5 })
    ]);
  }
  return rows;
}

function filasResumidas(informe: SaldoPorZonaInforme): RowInput[] {
  const rows: RowInput[] = [];
  for (const zona of informe.zonas) {
    rows.push([
      fila(nombreZona(zona.codigoRuta, zona.nombreRuta), 6, {
        fillColor: AZUL,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      })
    ]);
    for (const c of zona.clientes) {
      rows.push([
        String(c.nroCliente),
        c.nombre,
        [c.ciudad, c.direccion].map((x) => x?.trim()).filter(Boolean).join(' - '),
        c.telefono?.trim() ?? '',
        importe(c.saldoAnterior),
        { content: importe(c.saldo), styles: { fontStyle: 'bold' } }
      ]);
    }
    rows.push([
      fila(`Total Zona ${zona.nombreRuta}`, 5, { halign: 'right', fontStyle: 'bold', textColor: AZUL }),
      fila(formatCurrency(zona.total), 1, { halign: 'right', fontStyle: 'bold', textColor: AZUL })
    ]);
  }
  return rows;
}

export async function buildSaldoPorZonaPdfBlob(
  informe: SaldoPorZonaInforme,
  opts: SaldoPorZonaListadoOpts
): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  doc.setLineHeightFactor(1);
  const marginX = 30;
  let y = 40;

  doc.setFontSize(15);
  doc.setTextColor(...AZUL);
  doc.text('Resumen de Cuenta de Clientes por Zona', marginX, y);
  doc.setTextColor(0, 0, 0);
  y += 18;
  doc.setFontSize(10);
  doc.text(
    `Saldo al ${formatDate(informe.fechaHasta)}  —  Movimientos desde: ${formatDate(informe.fechaDesde)}`,
    marginX,
    y
  );
  y += 14;
  if (!opts.incluirMovimientos) {
    doc.setFontSize(9);
    doc.text('Resumido (sin movimientos)', marginX, y);
    y += 12;
  }
  doc.setFontSize(8);
  doc.setTextColor(...GRIS_TEXTO);
  doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, y);
  doc.setTextColor(0, 0, 0);
  y += 12;

  const detallado = opts.incluirMovimientos;
  const head = detallado
    ? [['Fecha emisión', 'Comprobante', 'Descripción', 'Saldo comp.', 'Debe', 'Haber', 'Saldo']]
    : [['Nro.', 'Cliente', 'Localidad', 'Teléfono', 'Saldo anterior', 'Saldo']];
  const columnas = head[0].length;

  autoTable(doc, {
    startY: y,
    theme: 'plain',
    head,
    body: detallado ? filasDetalladas(informe) : filasResumidas(informe),
    foot: [
      [
        fila('Total general del informe', columnas - 1, { halign: 'right' }),
        fila(formatCurrency(informe.totalGeneral), 1, { halign: 'right' })
      ]
    ],
    showFoot: 'lastPage',
    showHead: 'everyPage',
    margin: { left: marginX, right: marginX, bottom: 30 },
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 1.5, right: 3, bottom: 1.5, left: 3 },
      overflow: 'linebreak',
      valign: 'middle'
    },
    headStyles: {
      fillColor: [242, 242, 242],
      textColor: 0,
      fontStyle: 'bold',
      fontSize: 7.5
    },
    footStyles: {
      fillColor: AZUL,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9.5
    },
    columnStyles: detallado
      ? {
          0: { cellWidth: 58 },
          1: { cellWidth: 82 },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 68, halign: 'right' },
          4: { cellWidth: 68, halign: 'right' },
          5: { cellWidth: 68, halign: 'right' },
          6: { cellWidth: 74, halign: 'right' }
        }
      : {
          0: { cellWidth: 36 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 140 },
          3: { cellWidth: 70 },
          4: { cellWidth: 70, halign: 'right' },
          5: { cellWidth: 74, halign: 'right' }
        }
  });

  const totalPaginas = doc.getNumberOfPages();
  const anchoPag = doc.internal.pageSize.getWidth();
  const altoPag = doc.internal.pageSize.getHeight();
  for (let p = 1; p <= totalPaginas; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`Página ${p} de ${totalPaginas}`, anchoPag / 2, altoPag - 16, { align: 'center' });
  }
  doc.setTextColor(0, 0, 0);

  return doc.output('blob');
}

export function exportSaldoPorZonaExcel(informe: SaldoPorZonaInforme, opts: SaldoPorZonaListadoOpts): void {
  const workbook = XLSX.utils.book_new();

  const resumen: Array<Record<string, string | number>> = [];
  for (const zona of informe.zonas) {
    for (const c of zona.clientes) {
      resumen.push({
        'Cód. Zona': zona.codigoRuta ?? '',
        Zona: zona.nombreRuta,
        'Nro. Cliente': c.nroCliente,
        Cliente: c.nombre,
        Provincia: c.provincia ?? '',
        Ciudad: c.ciudad ?? '',
        Dirección: c.direccion ?? '',
        Teléfono: c.telefono ?? '',
        'Saldo anterior': c.saldoAnterior,
        'Saldo a fecha': c.saldo
      });
    }
    resumen.push({ Zona: `Total Zona ${zona.nombreRuta}`, 'Saldo a fecha': zona.total });
  }
  resumen.push({ Zona: 'Total general del informe', 'Saldo a fecha': informe.totalGeneral });
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(resumen), 'SaldoPorZona');

  if (opts.incluirMovimientos) {
    const movimientos: Array<Record<string, string | number>> = [];
    for (const zona of informe.zonas) {
      for (const c of zona.clientes) {
        for (const m of c.movimientos) {
          movimientos.push({
            Zona: zona.nombreRuta,
            'Nro. Cliente': c.nroCliente,
            Cliente: c.nombre,
            'Fecha emisión': formatDate(m.fechaEmision),
            Comprobante: m.comprobante,
            Descripción: m.descripcion ?? '',
            'Saldo comp.': m.saldoComp,
            Debe: m.debe,
            Haber: m.haber,
            Saldo: m.saldo
          });
        }
      }
    }
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(movimientos), 'Movimientos');
  }

  XLSX.writeFile(workbook, `saldo_clientes_por_zona_${informe.fechaHasta.slice(0, 10).replace(/-/g, '')}.xlsx`);
}
